import { formatISO } from 'date-fns';
import { useMemo } from 'react';
import { gql, useQuery } from 'urql';

import { RangeDate, RgCraAutomationLegacyHistoryItem } from '@/globalTypes';
import { usePlayer, useQueryParams } from '@/hooks';
import { PlayerRgAutomationHistoryLegacyQuery } from './__generated__/usePlayerRgAutomationHistoryLegacy';
import { queryParams } from './queryParams';

export type LegacyAutomationHistoryEntry = RgCraAutomationLegacyHistoryItem;

const QUERY = gql`
  query PlayerRgAutomationHistoryLegacy(
    $after: String
    $before: String
    $first: Int
    $last: Int
    $playerGlobalId: ID!
    $showSuspended: Boolean
    $processedAt: RangeDate
  ) {
    viewer {
      id
      rgAutomationHistory: rgCraAutomationV2LegacyHistory(
        after: $after
        before: $before
        first: $first
        last: $last
        playerGlobalId: $playerGlobalId
        showSuspended: $showSuspended
        processedAt: $processedAt
      ) {
        edges {
          node {
            eventId
            eventType
            extraInformation
            playerGlobalId
            processedAt
            regionId
            regionName
            riskId
            riskName
            riskLevelBefore
            riskLevelAfter
            suspendedBy
          }
        }
        pageInfo {
          endCursor
          hasNextPage
          startCursor
          hasPreviousPage
        }
      }
    }
  }
`;

export default function usePlayerRgAutomationHistoryLegacy({
  paused,
}: { paused?: boolean } = {}) {
  const [query, setQuery] = useQueryParams(queryParams);
  const { playerGlobalId } = usePlayer();

  const processedAt = useMemo((): RangeDate | undefined => {
    if (query.legacyFrom && query.legacyTo) {
      return {
        from: formatISO(query.legacyFrom),
        to: formatISO(query.legacyTo),
      };
    }
    return undefined;
  }, [query.legacyFrom, query.legacyTo]);

  const [{ data, fetching }, refresh] =
    useQuery<PlayerRgAutomationHistoryLegacyQuery>({
      query: QUERY,
      variables: {
        after: query.legacyAfter,
        before: query.legacyBefore,
        first: query.legacyFirst,
        last: query.legacyLast,
        playerGlobalId: playerGlobalId || '',
        showSuspended: query.legacyShowSuspended,
        processedAt,
      },
      pause: paused || !playerGlobalId,
    });

  const pageInfo = data?.viewer.rgAutomationHistory.pageInfo;

  return {
    refresh,
    fetching,
    defaultFilter: query,
    updateFilter: (values: {}) => setQuery(values, 'replaceIn'),
    nextPage: pageInfo?.hasNextPage
      ? () =>
          setQuery({
            ...query,
            legacyAfter: pageInfo.endCursor || undefined,
            legacyBefore: undefined,
          })
      : null,
    previousPage: pageInfo?.hasPreviousPage
      ? () =>
          setQuery({
            ...query,
            legacyAfter: undefined,
            legacyBefore: pageInfo.startCursor || undefined,
          })
      : null,
    history: data?.viewer.rgAutomationHistory?.edges?.map(
      (edge): LegacyAutomationHistoryEntry | undefined => edge?.node,
    ),
  };
}
